<template>
  <div class="gift-card-detail">
    <div class="gift-card-detail__headline">
      <p class="gift-card-detail__headline--pattern">
        {{ title }}
      </p>
      <div class="gift-card-detail__headline--detail">
        <p>
          発行数：<span class="gift-card-detail__headline--detail--bold">
            {{ publishAmount }}件
          </span>
        </p>
        <p>
          合計金額<span v-if="isTotalPriceIncludingTax">（税込）</span>：<span
            class="gift-card-detail__headline--detail--bold"
          >
            ¥{{ totalPrice.toLocaleString() }}
          </span>
        </p>
      </div>
    </div>
    <GiftCardItem
      class="gift-card-detail__gift-card"
      :gift-card-image-url="giftCardImageUrl"
      :sender-name="senderName"
      :message="message"
    />
    <div>
      <div class="gift-card-detail__item-sku-headline" @click="onClickToggle">
        <p class="gift-card-detail__item-sku-headline--title">
          選択中のギフト
        </p>
        <div class="gift-card-detail__item-sku-headline--action">
          <ChevronUp
            v-show="showItemSkus"
            class="gift-card-detail__item-sku-headline--action--icon"
          />
          <ChevronDown
            v-show="!showItemSkus"
            class="gift-card-detail__item-sku-headline--action--icon"
          />
        </div>
      </div>
      <div v-show="showItemSkus" class="gift-card-detail__item-skus">
        <TicketConnectedMultiple
          v-for="(itemSkuContent, index) in itemSkuContents"
          :key="index"
          :image-url="itemSkuContent.imageUrl"
          :expired-date="itemSkuContent.expiredDate"
          :item-name="itemSkuContent.itemName"
          :brand-name="itemSkuContent.brandName"
          :is-including-tax="itemSkuContent.isIncludingTax"
          :item-sku-name="
            itemSkuContent.hasMultipleItemSkus ? itemSkuContent.itemSkuName : ''
          "
          :ticket-count="itemSkuContent.quantity"
          :price="itemSkuContent.price"
        />
      </div>
    </div>
    <div class="gift-card-detail__actions">
      <a
        class="gift-card-detail__actions--button"
        :href="`/bulk_order/gift_cards/${token}`"
        @click="onClickPreview"
      >
        <Eye width="20" height="20" />プレビュー
      </a>
      <button class="gift-card-detail__actions--button" @click="onClickCopy">
        <SendBackward width="20" height="20" />複製
      </button>
      <button class="gift-card-detail__actions--button" @click="onClickEdit">
        <EditOutline width="20" height="20" />編集
      </button>
      <button class="gift-card-detail__actions--button" @click="onClickDelete">
        <TrashOutline width="20" height="20" />削除
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import GiftCardItem from '../../../atoms/gift_cards/GiftCardItem.vue'
import TicketConnectedMultiple from '../../../atoms/tickets/TicketConnectedMultiple.vue'
import SendBackward from '../../../atoms/svg_icons/SendBackward.vue'
import EditOutline from '../../../atoms/svg_icons/EditOutline.vue'
import Eye from '../../../atoms/svg_icons/Eye.vue'
import TrashOutline from '../../../atoms/svg_icons/TrashOutline.vue'
import ChevronUp from '../../../atoms/svg_icons/ChevronUp.vue'
import ChevronDown from '../../../atoms/svg_icons/ChevronDown.vue'

const showItemSkus = ref(false)

const props = defineProps({
  token: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  publishAmount: {
    type: Number,
    required: true
  },
  totalPrice: {
    type: Number,
    required: true
  },
  isTotalPriceIncludingTax: {
    type: Boolean,
    required: true
  },
  giftCardImageUrl: {
    type: String,
    required: true
  },
  senderName: {
    type: String,
    required: true
  },
  message: {
    type: String,
    required: false,
    default: ''
  },
  itemSkuContents: {
    type: Array,
    required: true
  }
})

const emit = defineEmits([
  'click-preview',
  'click-copy',
  'click-edit',
  'click-delete'
])

const onClickToggle = () => {
  showItemSkus.value = !showItemSkus.value
}

const onClickPreview = () => emit('click-preview', props.token)
const onClickCopy = () => emit('click-copy', props.token)
const onClickEdit = () => emit('click-edit', props.token)
const onClickDelete = () => emit('click-delete', props.token)
</script>

<style lang="scss" scoped>
.gift-card-detail {
  background-color: $white;
  padding: 0 16px;
  box-sizing: border-box;

  & > * {
    border-bottom: 1px solid $gray-2;

    &:last-child {
      border-bottom: none;
    }
  }

  &__headline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;

    &--pattern {
      font-size: 1.4rem;
      font-weight: bold;
      line-height: 1.6;
    }
    &--detail {
      display: flex;
      gap: 8px;
      font-size: 1rem;
      line-height: 1.6;
      &--bold {
        font-weight: bold;
      }
    }
  }

  &__gift-card {
    padding: 16px 0px;
  }

  &__item-sku-headline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    cursor: pointer;

    &--title {
      font-size: 1.4rem;
      font-weight: bold;
      line-height: 1.6;
    }
    &--action {
      width: 24px;
      height: 24px;

      &--icon {
        fill: $black;
      }
    }
  }

  &__item-skus {
    & > * {
      border-bottom: 1px solid $gray-2;
      padding-top: 12px;
      padding-bottom: 12px;

      &:first-child {
        padding-top: 0;
      }

      &:last-child {
        border-bottom: none;
      }
    }
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;

    &--button {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
      color: $black;
      font-size: 1.2rem;
      line-height: 1.6;
      cursor: pointer;

      &:hover {
        opacity: 0.7;
      }
    }
  }
}
</style>
